import { captureRemixErrorBoundaryError, withSentry } from '@sentry/remix'
import '@mantine/core/styles/global.css'

import '@mantine/core/styles/UnstyledButton.css'
import '@mantine/core/styles/Button.css'

import '@mantine/core/styles/Popover.css'
import '@mantine/core/styles/Menu.css'

import { Links, Meta, Outlet, Scripts, ScrollRestoration, isRouteErrorResponse, useRouteError } from '@remix-run/react'
import { MetaFunction, json } from '@remix-run/node'

import '../app/styles/tailwind.css'
import '../app/styles/style.css'

import GoogleTagManager from '../app/components/GoogleTagManager'

import Header from '../app/components/header'
import Footer from '../app/components/footer'

import { useRouteLoaderData } from '@remix-run/react'
import { useState, useEffect } from 'react'

interface RootLoaderData {
  tl_env: string
}

export const loader = () => {
  return json<RootLoaderData>({
    tl_env: process.env.TL_ENV || 'development',
  })
}

export function ErrorBoundary() {
  const error = useRouteError()

  captureRemixErrorBoundaryError(error)

  if (isRouteErrorResponse(error)) {
    return (
      <html lang="en">
        <head>
          <Meta />
          <Links />
          <Scripts />
          <ScrollRestoration />
        </head>
        <body>
          <Header
            title="PAGE NOT FOUND"
            subtitle="The page you are looking for does not exist."
            backgroundimage="/images/header.png"
            ishomepage={false}
            shouldGetDarker={false}
            bgProp="bg-top"
          />
          <main className="tl-container mt-6 flex flex-col items-center text-center">
            {/* Add a descriptive message before the image */}
            <p className="text-body-lg font-semibold mb-4">Oops! We can't seem to find the page you're looking for.</p>
            {/* Display the 404 image */}
            <img src="/images/404.png" alt="404 - Page Not Found" className="w-1/2 max-w-md mb-4" />
            {/* Add additional text after the image if needed */}
            <p className="text-body mt-4 text-gray-600">
              It looks like the page you are trying to reach does not exist. You might have mistyped the URL, or the
              page has been moved.
            </p>
          </main>
          <Footer />
        </body>
      </html>
    )
  } else if (error instanceof Error) {
    return (
      <html lang="en">
        <head>
          <Meta />
          <Links />
          <Scripts />
          <ScrollRestoration />
        </head>
        <body>
          <div className="p-4">
            <h1>Error</h1>
            <p>{error.message}</p>
            <p>The stack trace is:</p>
            <pre>{error.stack}</pre>
          </div>
        </body>
      </html>
    )
  } else {
    return (
      <html lang="en">
        <head>
          <Meta />
          <Links />
          <Scripts />
          <ScrollRestoration />
        </head>
        <body>
          <h1>Unknown Error</h1>
        </body>
      </html>
    )
  }
}

export const headers = () => {
  return {
    'Cache-Control': `public, max-age=${60 * 10}, s-maxage=${60 * 60 * 24 * 7}`,
  }
}

export const meta: MetaFunction = ({ data }) => {
  return [
    { title: 'TalentLift Canada - Hire talent and transform lives.' },
    {
      name: 'description',
      content: 'Discover opportunities with TalentLift Canada, where we help you hire talent and transform lives.',
    },
    {
      property: 'og:title',
      content: 'TalentLift Canada - Hire talent and transform lives.',
    },
    {
      property: 'og:description',
      content: 'Discover opportunities with TalentLift Canada, where we help you hire talent and transform lives.',
    },
    {
      property: 'og:image',
      content: '/images/tl_logo_meta.png',
    },
    {
      property: 'og:url',
      content: 'https://www.talentlift.ca',
    },
  ]
}

export function App() {
  const data = useRouteLoaderData<RootLoaderData>('root')
  const tl_env = data?.tl_env || 'development'
  const [isClient, setIsClient] = useState(false)

  useEffect(() => {
    setIsClient(true)
  }, [])

  useEffect(() => {
    if (!isClient) return

    // Initialize Keela
    window.Keela = { id: "TAap6KmxAsPfAtFpt" }
  }, [isClient])

  return (
    <html lang="en">
      <head>
        <GoogleTagManager tl_env={tl_env} />
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1.0" />
        <Meta />
        <link
          rel="stylesheet"
          href="https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.5.2/css/all.min.css"
          integrity="sha512-SnH5WK+bZxgPHs44uWIX+LLJAJ9/2PkPKZ5QiAj6Ta86w+fsb2TkcmfRyVX3pBnMFcV7oQPJkl9QevSCWr3W6A=="
          crossOrigin="anonymous"
          referrerPolicy="no-referrer"
        />
        <link
          href="https://fonts.googleapis.com/css2?family=Lato:ital,wght@0,100;0,300;0,400;0,700;0,900;1,100;1,300;1,400;1,700;1,900&display=swap"
          rel="stylesheet"
        />
        <link rel="icon" href="/cropped-favicon-32x32.png" sizes="32x32" />
        <link rel="icon" href="/cropped-favicon-192x192.png" sizes="192x192" />
        <link rel="apple-touch-icon" href="/cropped-favicon-180x180.png" />
        <meta name="msapplication-TileImage" content="/cropped-favicon-270x270.png" />
        <Links />
        <script async type="text/javascript" src="https://cdn.keela.co/forms/keela-forms.min.js" />
      </head>
      <body className="font-lato subpixel-antialiased">
        <Outlet />
        <ScrollRestoration />
        <Scripts />
      </body>
    </html>
  )
}

export default withSentry(App)
